import React, { useEffect, useCallback } from 'react';
import CarouselModal from '../../../../../commons/components/CarouselModal/CarouselModal.jsx';
import {
  updateShouldOpenCarouselModal,
  selectShouldOpenCarouselModal,
} from '../../../store/slice';
import { RootState } from 'src/app/store';
import { connect } from 'react-redux';
import { LocalNotification } from '../types';
import { ConsoleLevelBannerMetricsPublisher } from 'src/commons/utils/metrics';
import { logWithTakt } from 'src/commons/utils/logWithTakt';

const DISMISSED_MODALS_LOCAL_STORAGE = 'dismissedModals';
const OPEN_MODAL_DELAY = 1000;

interface BannerTriggeredCarouselModalProps {
  banner: LocalNotification;
  modalTrigger: React.MutableRefObject<null>;
  shouldAutoOpenModal: boolean;
  setShouldAutoOpenModal: (...args: any[]) => any;
}

export const BannerTriggeredCarouselModal: React.VFC<
  BannerTriggeredCarouselModalProps
> = ({ banner, modalTrigger, shouldAutoOpenModal, setShouldAutoOpenModal }) => {
  const carouselModalId = banner.modal?.id;
  const carouselModalContainer = banner.modal?.container;

  const closeModal = useCallback(() => {
    const modalId = banner.modal?.id;
    const dismissedModals = JSON.parse(
      localStorage.getItem(DISMISSED_MODALS_LOCAL_STORAGE) || '[]',
    );
    if (!dismissedModals.includes(modalId)) {
      dismissedModals.push(modalId);
      localStorage.setItem(
        DISMISSED_MODALS_LOCAL_STORAGE,
        JSON.stringify(dismissedModals),
      );
    }
    setShouldAutoOpenModal(false);
    logWithTakt('consoleLevelModal', {
      modalId,
      status: 'close',
    });
    ConsoleLevelBannerMetricsPublisher.publishCounterMonitor(
      `modal-closed-${modalId}`,
      1,
    );
  }, [banner]);

  // show modal on user's first visit
  useEffect(() => {
    const dismissedModals = JSON.parse(
      localStorage.getItem(DISMISSED_MODALS_LOCAL_STORAGE) || '[]',
    );
    const hasDismissedModal =
      dismissedModals.findIndex((element) => element === banner.modal?.id) !==
      -1;

    if (hasDismissedModal) {
      return;
    }

    const timer = setTimeout(() => {
      setShouldAutoOpenModal(true);
      clearTimeout(timer);
    }, OPEN_MODAL_DELAY);
  }, [banner]);

  return (
    <CarouselModal
      data-test-id="carousel-modal"
      id={carouselModalId}
      isOpen={shouldAutoOpenModal}
      onClose={closeModal}
      toggleRef={modalTrigger.current}
      {...carouselModalContainer}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    shouldAutoOpenModal: selectShouldOpenCarouselModal(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setShouldAutoOpenModal: (shouldOpen) =>
      dispatch(updateShouldOpenCarouselModal(shouldOpen)),
  };
};

export const ConnectedBannerTriggeredCarouselModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BannerTriggeredCarouselModal);
